import { useState } from "react"
import { Users, LayoutDashboard, ShieldCheck, Medal, LogOut, Menu, X, Trash2 } from "lucide-react"
import { Link, useRoute, navigate } from "../lib/router"
import { deleteAccount } from "../lib/api"
import DeleteAccountModal from "./DeleteAccountModal"
import LangToggle from "./LangToggle"
import { useLanguage } from "../lib/LanguageContext"

export default function CoachSidebar() {
  const { path } = useRoute()
  const clubName = localStorage.getItem("clubName") || ""
  const userName = localStorage.getItem("name") || ""
  const [open, setOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const close = () => setOpen(false)
  const { t } = useLanguage()

  const handleDeleteAccount = async () => {
    setDeleting(true)
    if (await deleteAccount()) {
      localStorage.clear()
      navigate("/login")
    } else {
      setDeleting(false)
      setDeleteOpen(false)
    }
  }

  return (
    <div>
      {/* Mobile top bar */}
      <div className="flex items-center justify-between border-b border-white/10 bg-ink-800/80 px-4 py-3 lg:hidden">
        <div className="flex items-center gap-2">
          <img src="/vietnam_aquatics.png" alt="Logo" className="h-7 w-auto rounded-md bg-white/95 p-1" onError={(e) => { e.currentTarget.style.display = "none" }} />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400">{t("sidebar_coach_portal")}</span>
        </div>
        <button onClick={() => setOpen(true)} aria-label={t("nav_open_menu")} className="flex h-9 w-9 items-center justify-center rounded border border-white/15 text-paper-0">
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {/* Mobile backdrop */}
      {open && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={close} aria-hidden="true" />}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] shrink-0 flex-col overflow-y-auto border-r border-white/10 bg-ink-800 p-6 transition-transform duration-300 lg:sticky lg:top-0 lg:z-auto lg:h-screen lg:w-64 lg:max-w-none lg:translate-x-0 lg:bg-ink-800/50 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <button onClick={close} aria-label={t("nav_close_menu")} className="mb-6 flex h-11 w-11 items-center justify-center self-end text-mist-300 lg:hidden">
          <X className="h-5 w-5" />
        </button>

        <div className="mb-10 flex flex-col items-center gap-4 text-center">
          <img
            src="/vietnam_aquatics.png"
            alt="Logo"
            className="h-16 w-auto rounded-xl bg-white/95 p-2"
            onError={(e) => { e.currentTarget.style.display = "none" }}
          />
          <div className="flex flex-col items-center gap-1.5 mt-2">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-gold-400">{t("sidebar_coach_portal")}</span>
            {clubName && <span className="text-2xl font-bold text-paper-0">{clubName}</span>}
            {userName && <span className={`font-bold tracking-tight ${clubName ? 'text-base text-mist-300' : 'text-2xl text-paper-0'}`}>{userName}</span>}
          </div>
        </div>

        <nav className="flex flex-col gap-2 flex-1">
          <Link
            to="/coach/dashboard"
            onClick={close}
            className={`flex items-center gap-3 rounded px-4 py-2.5 text-base font-medium transition-colors ${path === "/coach/dashboard" || path === "/coach" ? "bg-gold-500/10 text-gold-400 font-semibold" : "text-mist-300 hover:bg-white/5 hover:text-paper-0"}`}
          >
            <LayoutDashboard className="h-5 w-5" />
            {t("sidebar_dashboard")}
          </Link>
          <Link
            to="/coach/athletes"
            onClick={close}
            className={`flex items-center gap-3 rounded px-4 py-2.5 text-base font-medium transition-colors ${path === "/coach/athletes" ? "bg-gold-500/10 text-gold-400 font-semibold" : "text-mist-300 hover:bg-white/5 hover:text-paper-0"}`}
          >
            <Users className="h-5 w-5" />
            {t("sidebar_athletes")}
          </Link>
          <Link
            to="/coach/verification"
            onClick={close}
            className={`flex items-center gap-3 rounded px-4 py-2.5 text-base font-medium transition-colors ${path === "/coach/verification" ? "bg-gold-500/10 text-gold-400 font-semibold" : "text-mist-300 hover:bg-white/5 hover:text-paper-0"}`}
          >
            <ShieldCheck className="h-5 w-5" />
            {t("sidebar_verification")}
          </Link>
          <Link
            to="/coach/results"
            onClick={close}
            className={`flex items-center gap-3 rounded px-4 py-2.5 text-base font-medium transition-colors ${path === "/coach/results" ? "bg-gold-500/10 text-gold-400 font-semibold" : "text-mist-300 hover:bg-white/5 hover:text-paper-0"}`}
          >
            <Medal className="h-5 w-5" />
            {t("sidebar_coach_results")}
          </Link>
        </nav>

        <div className="mt-auto border-t border-white/10 pt-6 flex flex-col gap-3">
          <LangToggle fullWidth />
          <Link
            to="/login"
            onClick={() => { localStorage.clear(); close() }}
            className="group flex w-full items-center gap-3 rounded border border-red-500/20 bg-red-500/5 px-4 py-2.5 text-base font-semibold text-red-400 transition-all duration-300 hover:border-red-500/40 hover:bg-red-500 hover:text-white hover:shadow-[0_0_15px_rgba(239,68,68,0.25)]"
          >
            <div className="flex items-center justify-center transition-transform group-hover:-translate-x-1">
              <LogOut className="h-5 w-5" />
            </div>
            {t("sidebar_sign_out")}
          </Link>
          <button
            onClick={() => setDeleteOpen(true)}
            className="mt-2 flex w-full items-center gap-3 rounded px-4 py-2.5 text-base font-medium text-mist-500 transition-colors hover:text-red-400"
          >
            <Trash2 className="h-5 w-5" />
            {t("sidebar_delete_account")}
          </button>
        </div>
      </aside>

      <DeleteAccountModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteAccount}
        busy={deleting}
      />
    </div>
  )
}
